import React, {
  useEffect,
  useMemo,
  useState,
  MouseEventHandler,
  ChangeEvent,
  ReactNode,
} from 'react';
import PropTypes from 'prop-types';
import './style.scss';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { ReduxAssistantState, uiActions } from 'careand-redux/slices/ui/ui';
import ChatWindow from 'components/Chat/ChatWindow';
import { useAppSelector } from 'careand-redux/store';
import ChatUtils from 'services/ChatUtils';
import moment from 'moment-timezone';
import useAssistant from 'hooks/useAssistant';
import Icon from 'components/Utils/NewIcon';
import { CustomLoadingDataSpinner } from 'components/Common/LoadingDataSpinner';
import AdminUtils from '../../../services/AdminUtils';
import ReactMarkdown from 'react-markdown';
import { GetStaff, IsAdmin, IsPhysician } from '../../../careand-redux/selectors';
import PatientSearchField from '../../Patient/PatientSearchField';
import { Patient, Physician } from '../../../generated/api';
import { getAIContext } from '../../../services/Spotlight/SpotlightAIContext';
import { Popover } from '@material-ui/core';
import DefaultModal from '../../Utils/DefaultModal';
import DefaultButton from '../../Utils/NewDefaultButton';
import DefaultTextField from '../../Utils/DefaultTextField';
import StackItemsView from '../../Common/StackItemsView';
import PerformanceUtils from '../../../services/PerformanceUtils';
import { api } from '../../../careand-redux/api/ApiThunks';

type Props = {
  setFullScreen: (arg: boolean) => void;
  reduxAssistant?: ReduxAssistantState;
  visible?: boolean;
};
const AssistantChat = ({ setFullScreen, reduxAssistant, visible }: Props) => {
  const dispatch = useAppDispatch();
  const [patient, setPatient] = useState<Patient | null>(null);
  useEffect(() => {
    if (reduxAssistant && visible) {
      dispatch(uiActions.updateAssistant({ id: reduxAssistant.id, unread: 0 }));
    }
  }, [dispatch, reduxAssistant, visible]);

  const ownUser = useAppSelector((state) => state.authentication.user);
  const [streamingKey] = useState(`chat-assistant-${moment().unix()}`);

  const isAdmin = useAppSelector(IsAdmin);

  const {
    streamingText,
    messages,
    chatAssistant,
    loadingReply,
    stopRequest,
    chatSavedPrompt,
    replySuggestions,
    regenerateSuggestions,
    loadingReplySuggestions,
  } = useAssistant(reduxAssistant?.id ?? streamingKey, true, !isAdmin && visible);

  const reduxAssistantFirstMessageType = reduxAssistant?.firstMessage?.type;
  const savedPromptData =
    reduxAssistant?.firstMessage?.type === 'promptData'
      ? reduxAssistant?.firstMessage?.savedPromptData
      : undefined;
  const assistantMessage =
    reduxAssistant?.firstMessage?.type === 'message'
      ? reduxAssistant?.firstMessage?.assistantMessage
      : undefined;
  const reduxAssistantId = reduxAssistant?.id;

  useEffect(() => {
    if (!reduxAssistantFirstMessageType) {
      return;
    }

    dispatch(
      uiActions.updateAssistant({
        id: reduxAssistant.id,
        firstMessage: undefined,
      }),
    );

    if (assistantMessage) {
      sendMessage(assistantMessage, true);
    } else if (savedPromptData) {
      chatSavedPrompt(savedPromptData);
    }
  }, [
    dispatch,
    reduxAssistantId,
    reduxAssistantFirstMessageType,
    savedPromptData,
    assistantMessage,
  ]);

  const groupedMessages = useMemo(() => {
    const sectionRegex = /### HR_SECTION (.*?)\n([\s\S]*?)(?=\n={80,}|$)/g;

    let allMessages = [...messages].map((message) => {
      const sections = [...message.content.matchAll(sectionRegex)].map((match) => ({
        title: match[1],
        content: match[2]!.trim(),
      }));

      let content = message.content
        .replace(sectionRegex, '')
        .replace(/\n={80,}\n?/g, '')
        .trim();

      return {
        ...message,
        content,
        sections: sections as typeof sections | undefined,
      };
    });

    if (streamingText) {
      allMessages.push({
        role: 'assistant',
        content: streamingText,
        date: moment().unix(),
        sections: undefined,
      });
    } else if (loadingReply) {
      allMessages.push({
        role: 'assistant',
        content: '⏳',
        date: moment().unix(),
        sections: undefined,
      });
    }

    return ChatUtils.getAssistantGroupedMessages(allMessages as any, ownUser);
  }, [messages, streamingText, ownUser, loadingReply]);

  function onClose() {
    if (setFullScreen) setFullScreen(false);
    dispatch(uiActions.showAssistant(undefined));
  }

  function sendMessage(text = '', clear: boolean) {
    text = text.trim();
    if (groupedMessages.length === 0 && patient?.id) {
      chatSavedPrompt({
        name: 'Patient Records Chat',
        savedPromptRequest: {
          patient_id: patient.id,
          fields: [{ name: 'prompt', value: text }],
        },
      });
    } else {
      chatAssistant(text, clear);
    }
    return true;
  }

  if (visible === false) return null;

  return (
    <div className="row assistant-chat">
      <div className="under-layer" onClick={onClose} />
      <ChatWindow
        groupedMessages={groupedMessages}
        allowUpload={false}
        form="ChatWindow-assistant"
        assistant={true}
        sendMessage={sendMessage}
        onClose={onClose}
        loadingReply={loadingReply}
        disabledInput={loadingReply}
        welcomeMessage={<WelcomeMessage patient={patient} setPatient={setPatient} />}
        replySuggestions={
          !loadingReply && (
            <ReplySuggestions
              suggestions={replySuggestions}
              sendMessage={sendMessage}
              regenerateSuggestions={regenerateSuggestions}
              loading={loadingReplySuggestions}
            />
          )
        }
        extraIcons={
          loadingReply && (
            <Icon
              alt="Interrupt Assistant"
              showTooltip={true}
              width="20px"
              src="circle-stop"
              className="icon-stop-assistant"
              onClick={stopRequest}
            />
          )
        }
        renderTopBar={() => null}
      />
    </div>
  );
};

interface ReplySuggestionsProps {
  suggestions: string[];
  sendMessage: (text: string, clear: boolean) => void;
  regenerateSuggestions: () => void;
  loading: boolean;
}

interface ReplySuggestionsProps {
  suggestions: string[];
  sendMessage: (text: string, clear: boolean) => void;
  regenerateSuggestions: () => void;
  loading: boolean;
}

const ReplySuggestions: React.FC<ReplySuggestionsProps> = ({
  suggestions,
  sendMessage,
  regenerateSuggestions,
  loading,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [showFavoritesModal, setShowFavoritesModal] = useState(false);
  const user = useAppSelector(GetStaff);

  const handleFavoritesClick: MouseEventHandler<HTMLDivElement> = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFavoritesClose = () => {
    setAnchorEl(null);
  };

  const handleFavoriteSelect = (prompt: string) => {
    sendMessage(prompt, false);
    handleFavoritesClose();
  };

  const handleManageFavorites = () => {
    setShowFavoritesModal(true);
    handleFavoritesClose();
  };

  const open = Boolean(anchorEl);
  const id = open ? 'favorites-popover' : undefined;

  return (
    <div className="grid gap-1 pt-6 reply-suggestions">
      <div onClick={handleFavoritesClick} style={{ width: '100%' }}>
        <ReplySuggestion
          key="favorites"
          text="Your favorite prompts"
          icon="star"
          onClick={() => {}}
        />
      </div>
      {suggestions?.length > 0 && (
        <>
          {suggestions.map((it, index) => (
            <ReplySuggestion key={index} text={it} onClick={() => sendMessage(it, false)} />
          ))}
          <div className="buttons-wrapper">
            <div className="regen-suggestions-button" onClick={regenerateSuggestions}>
              {loading ? (
                <CustomLoadingDataSpinner
                  loading
                  color="#2693d1"
                  style={{ width: '12px', height: '12px' }}
                />
              ) : (
                <Icon src="refresh" width="12px" />
              )}
              Refresh Suggestions
            </div>
          </div>
        </>
      )}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleFavoritesClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <div className="favorites-popover">
          {user?.favorite_prompts?.map((prompt, index) => (
            <div
              key={index}
              className="favorite-prompt"
              onClick={() => handleFavoriteSelect(prompt)}
            >
              {prompt.length > 150 ? prompt.substring(0, 147) + '...' : prompt}
            </div>
          ))}
          <div className="favorite-prompt manage-favorites" onClick={handleManageFavorites}>
            <Icon src="gear" width="15px" className="mr-2" />
            Manage Favorites
          </div>
        </div>
      </Popover>
      {showFavoritesModal && (
        <DefaultModal
          className="fit-modal favorites-prompts-modal"
          title="Manage Favorite Prompts"
          wrap={true}
          padding={false}
          content={<FavoritePromptsManager />}
          isOpen={showFavoritesModal}
          onRequestClose={() => setShowFavoritesModal(false)}
        />
      )}
    </div>
  );
};

const FavoritePromptsManager: React.FC = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(GetStaff);
  const isAdmin = useAppSelector(IsAdmin);

  const [favorites, setFavorites] = useState<string[]>(user?.favorite_prompts || []);
  const [newPrompt, setNewPrompt] = useState<string>('');
  const [editIndex, setEditIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!PerformanceUtils.isEqual(user?.favorite_prompts, favorites)) {
      if (isAdmin) {
        dispatch(
          api.Admins.updateAdmin({
            id: user!.id!,
            admin: { ...user, favorite_prompts: favorites },
          }),
        );
      } else {
        dispatch(
          api.Physicians.updatePhysician({
            id: user!.id!,
            physician: { ...(user as Physician), favorite_prompts: favorites },
          }),
        );
      }
    }
  }, [dispatch, user, favorites, isAdmin]);

  const addPrompt = () => {
    if (newPrompt.trim()) {
      setFavorites([newPrompt.trim(), ...favorites]);
      setNewPrompt('');
    }
  };

  const startEdit = (favorite: string) => {
    let index = favorites.indexOf(favorite);
    setEditIndex(index);
    setNewPrompt(favorites[index]!);
  };

  const saveEdit = () => {
    if (editIndex !== null && newPrompt.trim()) {
      const updatedFavorites = [...favorites];
      updatedFavorites[editIndex] = newPrompt.trim();
      setFavorites(updatedFavorites);
      setEditIndex(null);
      setNewPrompt('');
    }
  };

  const deletePrompt = (favorite: string) => {
    const updatedFavorites = favorites.filter((current) => favorite !== current);
    setFavorites(updatedFavorites);
  };

  return (
    <div className="favorite-prompts-manager">
      <div className="add-prompt">
        <DefaultTextField
          value={newPrompt}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setNewPrompt(e.target.value)}
          placeholder="Enter a new favorite prompt"
        />
        <div>
          <DefaultButton
            onClick={editIndex !== null ? saveEdit : addPrompt}
            name={editIndex !== null ? 'Save' : 'Add'}
            className="blue"
          />
        </div>
      </div>
      <ul className="favorites-list">
        <StackItemsView
          stack={favorites}
          onDeleteItem={deletePrompt}
          describeItem={(favorite: string) => favorite}
          onEditItem={startEdit}
          onMoveEnd={(list: string[]) => {
            setFavorites(list);
          }}
          itemKey={(item: string) => item}
          hideStatus={true}
        />
      </ul>
    </div>
  );
};

interface WelcomeMessageProps {
  patient: Patient | null;
  setPatient: (patient: Patient | null) => void;
}

const WelcomeMessage: React.FC<WelcomeMessageProps> = ({ patient, setPatient }) => {
  const admins = useAppSelector((state) => state.admins.byId);
  const assistant = AdminUtils.getAssistantUser(admins);
  const name = AdminUtils.getName(assistant);

  const patientContext = getAIContext();

  const isPhysician = useAppSelector((state) => IsPhysician(state));

  return (
    <div className="chat-message welcome-message">
      <div className="border" />
      <div className="message-block">
        <span className="name">{name}</span>
        <pre className="message">
          <ReactMarkdown>{DEFAULT_MESSAGE}</ReactMarkdown>
          {isPhysician && (
            <>
              <ReactMarkdown>{DEFAULT_MESSAGE_PHYSICIAN}</ReactMarkdown>
              <PatientSearchField
                patient={patient}
                //@ts-expect-error
                defaultResults={patientContext?.patient ? [patientContext.patient] : []}
                onSelect={(patient) => setPatient(patient)}
                hideTitle
              />
            </>
          )}
          How can I assist you today? 🌟
        </pre>
      </div>
    </div>
  );
};

AssistantChat.propTypes = {
  visible: PropTypes.bool,
};

const DEFAULT_MESSAGE_PHYSICIAN = `## 🔎 Patient Context
To provide patient-specific assistance, please enter a patient name or ID in the search field below. This will allow me to access their health records and provide contextualized support.`;

const DEFAULT_MESSAGE = `# 👋 Hello! I'm Carey, your Care& AI Assistant

As part of the Care& Family Health team, I'm here to streamline your daily workflow and support exceptional patient care. I can help with:

## 📋 Clinical Support
- Quick access to medical information
- Patient education materials
- Clinical documentation assistance

## 💼 Operations & Admin
- Care& protocols and procedures
- Workflow optimization
- Communication templates

## 🔍 Custom Solutions
- Real-time information lookups
- Process guidance
- Care& specific queries

I combine Care&'s healthcare expertise with AI efficiency to support your practice. While I'm not a substitute for clinical judgment, I'm here to make your work day smoother and more productive.`;

function ReplySuggestion({
  text,
  onClick,
  icon = 'arrow-right',
}: {
  text: string;
  onClick: (text: string) => void;
  icon?: string;
}) {
  return (
    <div className="reply-suggestion" onClick={() => onClick(text)}>
      <div className="content">{text}</div>
      <Icon src={icon} width="16px" />
    </div>
  );
}
export default AssistantChat;
